import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { readViewCode } from "../../lib/view.ts";

/**
 * Вход в просмотр кабинета преподавателя по ссылке из панели, `/view#<код>`
 * (раздел 12.4 `docs/admin-panel-design.md`).
 *
 * Код живёт минуту и гасится первым использованием. ⚠️ Фрагмент стирается из
 * адреса СРАЗУ по прочтении, до похода в сеть: погашенный код бесполезен, но
 * оставлять его в истории браузера и в адресной строке незачем.
 *
 * Провайдер сессии на этом адресе `getMe` не спрашивает (`lib/session.tsx`),
 * поэтому формы входа до конца обмена здесь нет. Отказ обмена возвращает
 * провайдеру правду через `recheck`.
 */
export function ViewPage() {
  const navigate = useNavigate();
  const { state, adopt, recheck } = useSession();
  const [failed, setFailed] = useState(false);
  /** Кода во фрагменте не было: чаще всего это перезагрузка уже открытого просмотра. */
  const [noCode, setNoCode] = useState(false);
  // StrictMode в деве монтирует эффект дважды, а обмен кода не чистое чтение:
  // второй прогон нашёл бы уже стёртый фрагмент и показал бы отказ
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = readViewCode(window.location.hash);
    window.history.replaceState(window.history.state, "", window.location.pathname);
    if (code === null) {
      setNoCode(true);
      setFailed(true);
      recheck();
      return;
    }

    api
      .signInWithViewCode(code)
      .then((me) => {
        // Cookie в браузере теперь чужая: вкладки кабинета, открытые раньше,
        // обязаны это узнать, иначе пишут доску в свою базу по чужой сессии.
        // Весть с `userId:view` рассылает сам `adopt` (`lib/session.tsx`)
        adopt(me);
        void navigate("/", { replace: true });
      })
      .catch(() => {
        // Отказ у обмена один на все причины, различать нечего
        setFailed(true);
        recheck();
      });
  }, [adopt, recheck, navigate]);

  const signed = state.status === "signed";
  // Перезагрузка вкладки, где просмотр уже идёт: код погашен, а сессия жива
  if (noCode && signed && state.me.viewer) return <Navigate to="/" replace />;

  return (
    <main className="lobby">
      <h1>Просмотр кабинета</h1>
      {failed ? (
        <>
          <p className="err">
            Ссылка просмотра устарела или уже использована, выпустите новую в панели.
          </p>
          {signed ? (
            <div className="lobby__row">
              <Link className="btn" to="/" replace>
                В кабинет
              </Link>
            </div>
          ) : null}
        </>
      ) : (
        <p className="muted">Открываем кабинет…</p>
      )}
    </main>
  );
}
